import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';

const SCRAP_KEY = 'dait_scrapped_campaigns';

function loadScraps() {
  try { return JSON.parse(localStorage.getItem(SCRAP_KEY)) ?? []; }
  catch { return []; }
}

const ScrapContext = createContext(null);

export function ScrapProvider({ children }) {
  const { isLoggedIn } = useAuth();
  const [scraps, setScraps] = useState(loadScraps);

  // 저장 목록은 계정에 딸린 데이터다. localStorage에만 남겨두면
  // 로그아웃·토큰 만료 뒤에도 저장된 것처럼 보이고,
  // 같은 브라우저에서 다른 계정으로 로그인하면 이전 사용자의 목록이 비친다.
  useEffect(() => {
    if (isLoggedIn) return;
    setScraps([]);
    localStorage.removeItem(SCRAP_KEY);
  }, [isLoggedIn]);

  // 비로그인 상태에서는 어떤 것도 저장된 것으로 보이지 않아야 한다
  const isScrapped = useCallback(
    (id) => isLoggedIn && scraps.some((c) => c.id === id),
    [scraps, isLoggedIn]
  );

  const scrap = useCallback((campaign) => {
    setScraps((prev) => {
      if (prev.some((c) => c.id === campaign.id)) return prev;
      const next = [...prev, { ...campaign, scrappedAt: new Date().toISOString() }];
      localStorage.setItem(SCRAP_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const unscrap = useCallback((id) => {
    setScraps((prev) => {
      const next = prev.filter((c) => c.id !== id);
      localStorage.setItem(SCRAP_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  return (
    <ScrapContext.Provider value={{ scraps, isScrapped, scrap, unscrap }}>
      {children}
    </ScrapContext.Provider>
  );
}

export function useScraps() {
  return useContext(ScrapContext);
}
